// How the ridden bike moves through the world (bike plan 3, task 3), pure so node tests cover it: it speeds up
// to its top speed in RIDE.ACCEL_S and rolls to a stop in RIDE.COAST_S along its last direction, turning at once
// when a new direction is pressed (it keeps its speed); a move a wall swallows stops it; and a remote rider's
// speed, read off its interpolated path, holds across the gaps between packets.
import { stepSpeed } from './bikeRide.mjs';

// One tick. input {dx, dy} or null (nothing pressed); dir is the unit direction it rolls along. blockedDir is the
// direction a wall last stopped it in: held into that wall, it stays stopped and the rider does not pedal (the
// stop pose, turned only), as the walk does; any other direction rides off at once.
export function rideStep({ speed, dir, input, top, dt, blockedDir = null }) {
  const len = input ? Math.hypot(input.dx, input.dy) : 0;
  if (len > 0) {
    const want = { dx: input.dx / len, dy: input.dy / len };
    if (blockedDir && want.dx * blockedDir.dx + want.dy * blockedDir.dy > 0.999) {
      return { speed: 0, dir: want, pushing: false };
    }
    return { speed: stepSpeed(speed, top, top, dt), dir: want, pushing: true };
  }
  return { speed: stepSpeed(speed, 0, top, dt), dir, pushing: false };
}

// Ridden into the next screen (street, house, Podwórko), the bike goes on at the speed and heading it had: no
// speeding up again from a standstill (owner G11, 2026-10-25). The speed goes as a share of the top speed (each
// screen's perspective sets its own top in pixels), no more than all of it; with no heading it is at rest (a
// reload: the session keeps these in memory only).
export function carriedRide({ share = 0, dir = null } = {}, top) {
  if (!dir) return { speed: 0, dir: null };
  return { speed: Math.max(0, Math.min(share, 1)) * top, dir };
}

// A move that went under a quarter of the way it meant to (and meant more than half a pixel) hit something.
export const BLOCKED_SHARE = 0.25;
export function blockedStop(moved, intended) {
  return intended > 0.5 && moved < BLOCKED_SHARE * intended;
}

// A remote rider: the interpolation stands still between packets (~10 Hz), so a still frame within
// REMOTE_HOLD_MS keeps the last speed, and the foot only goes down after a real stop. They pedal while they move
// (their keys are not sent: a remote rider's 0.5 s coast is drawn pedalling). held: {speed, stillMs} or null.
export const REMOTE_HOLD_MS = 250;
export function speedFromPath(prev, cur, dtMs, held) {
  const dist = Math.hypot(cur.x - prev.x, cur.y - prev.y);
  if (dist > 0 && dtMs > 0) {
    const speed = dist / (dtMs / 1000);
    return { speed, pedalling: true, held: { speed, stillMs: 0 } };
  }
  const stillMs = (held?.stillMs ?? 0) + dtMs;
  const speed = held && stillMs < REMOTE_HOLD_MS ? held.speed : 0;
  return { speed, pedalling: speed > 0, held: { speed: held?.speed ?? 0, stillMs } };
}
